/**
 * APP-SHELL DETECTION — is this document running inside an installed app?
 *
 * WHY THIS EXISTS. The Android build is a Bubblewrap TWA and the iOS build is a
 * PWABuilder WKWebView wrapper: both are thin shells around the SAME live site,
 * shipping no UI of their own (see `android/README.md`). So "the app" and "the
 * mobile website" are one document, one stylesheet, one component tree — and
 * the only way to give the app its own character is to know, at runtime, which
 * shell is rendering us. The verdict is published as `data-app-shell` on
 * `<html>` and read by stylesheets as `[data-app-shell="app"]`.
 *
 * THERE IS NO SINGLE SIGNAL THAT COVERS ALL FOUR SHELLS. This is the whole
 * reason `resolveAppShell` is layered rather than a one-line media query:
 *
 *   Android TWA          `display-mode: standalone`. Has NO custom user agent —
 *                        Bubblewrap gives no hook for one — so a UA-only
 *                        approach cannot see Android at all.
 *   iOS WKWebView        A plain web view. It is NOT Safari's home-screen web
 *   (PWABuilder)         app, so `navigator.standalone` is undefined and
 *                        `display-mode` generally reports `browser`. The
 *                        reliable signal is a UA token the native project
 *                        injects — see NATIVE_UA_TOKEN below.
 *   iOS Safari A2HS      `navigator.standalone === true` (all versions);
 *                        `display-mode: standalone` only from iOS 16.4.
 *   Desktop PWA          `display-mode: standalone`.
 *   Browser tab          None of the above. This is the DEFAULT.
 *
 * `web` IS THE FAIL-SAFE DEFAULT, AND THAT DIRECTION IS DELIBERATE. The root
 * layout renders `data-app-shell="web"` as a literal (no cookie, no header
 * sniffing — so no route loses static rendering, per CLAUDE.md §6) and nothing
 * here ever downgrades; detection only UPGRADES to `app`. If detection is
 * blocked, throws or never runs, every surface falls back to the browser
 * presentation, which is the complete one. The inverse default would hide the
 * mobile action bar while its desktop counterpart is also hidden below `md`,
 * losing the action outright — a functional defect, not a cosmetic one.
 *
 * =========================================================================
 * WHY THE WORK IS SPLIT IN TWO, AND WHY THIS SHAPE AND NOT THE OBVIOUS ONE
 * =========================================================================
 *
 * The obvious shape is one inline pre-paint <script> carrying the whole
 * resolver. It was built that way first and it is WRONG, for two independent
 * measured reasons:
 *
 *  1. AN INLINE SCRIPT DOES NOT ALWAYS RUN. On a `notFound()` render the
 *     script element arrives at body index 16 rather than index 1, because
 *     React materialises that subtree from the flight payload via DOM
 *     insertion instead of HTML parsing — and a DOM-INSERTED <script> NEVER
 *     EXECUTES, by browser rule. Measured: `data-app-shell-signals` absent on
 *     every not-found document while present on every ordinary one. The app
 *     would therefore render the BROWSER header on a 404, which is the one
 *     screen where it hurts most: there is no back control in the header and
 *     no browser back button either, so the user is stranded.
 *
 *  2. THE LOGIC COULD NOT BE SHARED. Reusing this module's resolver from a
 *     snippet string means `new Function(...)`, and production CSP is
 *     `script-src 'self' 'nonce-…' 'strict-dynamic'` with NO `'unsafe-eval'`
 *     (src/proxy.ts) — so it is blocked in the only environment that matters.
 *     Hand-copying the logic into the string would give two implementations of
 *     the same rules, free to drift.
 *
 * So the split is:
 *
 *     APP_SHELL_BOOT_SNIPPET   Pre-paint, and DELIBERATELY LOGIC-FREE: it
 *                              reads the remembered verdict and applies it.
 *                              Nothing to duplicate, nothing to drift.
 *     resolveAppShell()        The real resolver. Ordinary, typed, unit-tested
 *                              TypeScript, run from `AppShellProbe` on mount.
 *
 * The cost is one frame of browser presentation on the FIRST EVER launch in a
 * given shell, before anything has been remembered. Every launch after that is
 * flash-free, because the snippet applies the cached verdict before paint. A
 * one-frame flash once, in exchange for a resolver that always runs and is
 * actually testable, is the right trade — and it is the only ordering that
 * survives both the not-found path and the CSP.
 */

/** Stamped on `<html>`. Read by stylesheets as `[data-app-shell="app"]`. */
export const APP_SHELL_ATTR = "data-app-shell";

/** Records which signals fired, for remote-debugging a chromeless shell. */
export const APP_SHELL_SIGNALS_ATTR = "data-app-shell-signals";

export const APP_SHELL_APP = "app";
export const APP_SHELL_WEB = "web";

/**
 * The token the native iOS project appends to its web view's user agent.
 *
 * THIS LIVES IN A REPO WE DO NOT CONTROL. PWABuilder's generated Xcode project
 * sets the WKWebView's `customUserAgent`; the token has to be added there, and
 * it is LOST every time that project is regenerated from PWABuilder. That trap
 * is the same class as the Bubblewrap ones `android/README.md` documents, so it
 * is written down here rather than remembered.
 *
 * Nothing breaks while the token is absent: iOS resolves through the remembered
 * `source=pwa` start_url instead, and if that also fails it falls back to the
 * browser presentation.
 */
export const NATIVE_UA_TOKEN = "NFTBazApp";

/**
 * Where a resolved `app` verdict is remembered.
 *
 * NEEDED BECAUSE THE START_URL SIGNAL IS COLD-START ONLY. The manifest's
 * `start_url` is `/?source=pwa` (both `src/app/manifest.ts` and
 * `android/twa-manifest.json`), so that query string is present on the FIRST
 * document of a session and on no subsequent navigation. For the iOS web view —
 * where it may be the only signal that fires — forgetting it would mean the app
 * character appears on the launch screen and vanishes on the first tap.
 *
 * It cannot leak into the user's browser: a WKWebView inside the app has its own
 * storage container, entirely separate from Safari's.
 */
export const APP_SHELL_STORAGE_KEY = "nftbaz-app-shell";

/** Query flag that forces the app presentation; matches the manifest start_url. */
const FORCE_APP_PARAM = "source=pwa";
/** Query flag that clears a remembered verdict. The way back out of `app`. */
const FORCE_WEB_PARAM = "source=web";

/**
 * The pre-paint snippet. LOGIC-FREE ON PURPOSE — see the module banner.
 *
 * All it does is apply an already-remembered verdict before first paint, so a
 * returning launch never flashes. It decides nothing, which is exactly why it
 * being a string is harmless: there is no rule here that could drift from
 * `resolveAppShell` below.
 *
 * `localStorage` THROWS on access (not merely returns null) in a
 * privacy-restricted web view, so the whole body is wrapped: a detection
 * failure must never take the document down with it.
 */
export const APP_SHELL_BOOT_SNIPPET = `(function(){try{if(localStorage.getItem(${JSON.stringify(
  APP_SHELL_STORAGE_KEY
)})==="1"){document.documentElement.setAttribute(${JSON.stringify(
  APP_SHELL_ATTR
)},${JSON.stringify(APP_SHELL_APP)})}}catch(e){}})();`;

/** The ambient facts detection reads. Injected so it can be tested. */
export type AppShellEnvironment = {
  userAgent: string;
  /** `true` only in iOS Safari's home-screen web app; absent elsewhere. */
  standalone?: boolean;
  /** Answers a `(display-mode: …)` query, or null where matchMedia is absent. */
  matchesDisplayMode: ((mode: string) => boolean) | null;
  /** `location.search`, including the leading `?`. */
  search: string;
  /** The remembered verdict, or null. `"throw"` models a blocked store. */
  storedVerdict: string | null;
};

export type AppShellVerdict = {
  isApp: boolean;
  /** Which signals fired, in evaluation order. Published for debugging. */
  signals: string[];
  /**
   * What the caller should do with the remembered verdict: persist it, clear
   * it, or leave it alone. Returned rather than performed so this function
   * stays free of side effects and therefore trivially testable.
   */
  persist: "set" | "clear" | "none";
};

/** `display-mode` values that all mean "launched as an installed app". */
const APP_DISPLAY_MODES = ["standalone", "fullscreen", "minimal-ui"] as const;

/**
 * Decide whether this environment is an installed app shell.
 *
 * PURE. It performs no DOM writes and no storage writes — the caller applies
 * `persist`. That is what lets every branch below be asserted directly in
 * `app-shell.test.ts` with no fake DOM.
 */
export function resolveAppShell(env: AppShellEnvironment): AppShellVerdict {
  const signals: string[] = [];
  let isApp = false;

  // 1. The token the native iOS shell injects. The only signal that works in a
  //    plain WKWebView, where display-mode reports `browser`.
  if (env.userAgent.includes(NATIVE_UA_TOKEN)) {
    isApp = true;
    signals.push("ua");
  }

  // 2. iOS Safari's home-screen web app. Strictly `=== true`: some engines
  //    define the property as `false`, which is not a signal.
  if (env.standalone === true) {
    isApp = true;
    signals.push("standalone");
  }

  // 3. Android TWA, desktop PWA, and iOS 16.4+ home-screen web apps.
  if (env.matchesDisplayMode) {
    for (const mode of APP_DISPLAY_MODES) {
      if (env.matchesDisplayMode(mode)) {
        isApp = true;
        signals.push(`dm:${mode}`);
        break;
      }
    }
  }

  // 4. Cold start from the manifest's own start_url.
  if (env.search.includes(FORCE_APP_PARAM)) {
    isApp = true;
    signals.push("starturl");
  }

  // The escape hatch, and the last word for THIS document. The only way a
  // browser can acquire the app presentation is by opening a URL that literally
  // carries `source=pwa`; this is how a user gets back out. It is not sticky —
  // the next launch re-detects through signals 1-3.
  if (env.search.includes(FORCE_WEB_PARAM)) {
    signals.push("reset");
    return { isApp: false, signals, persist: "clear" };
  }

  if (isApp) return { isApp, signals, persist: "set" };

  // No live signal: fall back to what a previous launch established. This is
  // the branch the iOS web view lands on for every navigation after the first.
  if (env.storedVerdict === "1") {
    signals.push("stored");
    return { isApp: true, signals, persist: "none" };
  }

  return { isApp: false, signals, persist: "none" };
}

/** Reads the live environment. Returns null when called outside a browser. */
export function readAppShellEnvironment(): AppShellEnvironment | null {
  if (typeof window === "undefined") return null;

  let storedVerdict: string | null = null;
  try {
    storedVerdict = window.localStorage.getItem(APP_SHELL_STORAGE_KEY);
  } catch {
    // Privacy-restricted web view. Detection still works from live signals.
    storedVerdict = null;
  }

  const nav = window.navigator as Navigator & { standalone?: boolean };

  return {
    userAgent: nav.userAgent || "",
    standalone: nav.standalone,
    matchesDisplayMode: window.matchMedia
      ? (mode: string) => window.matchMedia(`(display-mode: ${mode})`).matches
      : null,
    search: window.location.search || "",
    storedVerdict,
  };
}

/**
 * Apply a verdict to the document, and remember it.
 *
 * UPGRADE-ONLY on the attribute: a `web` verdict does NOT rewrite the
 * attribute, because the server already rendered `web` and rewriting it would
 * make this function able to undo a correct pre-paint `app` — the exact
 * regression the fail-safe direction exists to prevent. The one exception is an
 * explicit reset, which must be able to leave the app presentation.
 */
export function applyAppShellVerdict(verdict: AppShellVerdict): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;

  if (verdict.isApp) {
    root.setAttribute(APP_SHELL_ATTR, APP_SHELL_APP);
  } else if (verdict.persist === "clear") {
    root.setAttribute(APP_SHELL_ATTR, APP_SHELL_WEB);
  }

  root.setAttribute(APP_SHELL_SIGNALS_ATTR, verdict.signals.join(",") || "none");

  try {
    if (verdict.persist === "set") {
      window.localStorage.setItem(APP_SHELL_STORAGE_KEY, "1");
    } else if (verdict.persist === "clear") {
      window.localStorage.removeItem(APP_SHELL_STORAGE_KEY);
    }
  } catch {
    // Blocked storage: this launch is still correct, the next one re-detects.
  }
}
